// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type {
  CheckoutProps,
  CheckoutResponse as V3CheckoutResponse,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  useCheckout,
  useStoreSpecials,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Flex, Link, SwitchField, Typography } from '@iheartjane/reefer';
import { formatCurrency, pluralize } from '@iheartjane/utils';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import type { CheckoutFormFieldsDevice } from '@jane/kiosk/components';
import {
  BaseCustomerForm,
  BirthDateField,
  CartCheckoutError,
  FirstNameField,
  LastInitialField,
  LastNameField,
  NotesField,
  PhoneNumberField,
  SubmitCustomerInfoButton,
} from '@jane/kiosk/components';
import type {
  CreateCheckoutParams,
  RootLoaderType,
  CheckoutResponse,
} from '@jane/kiosk/data-access';
import {
  updateLocalCartUuid,
  useCheckoutRefusal,
  useSubmitOrder,
} from '@jane/kiosk/data-access';
import { useKioskCart, useTypedRouteLoaderData } from '@jane/kiosk/hooks';
import {
  useCartInfoForTracking,
  useProductTracking,
  useSessionContext,
} from '@jane/kiosk/providers';
import {
  buildKioskCheckoutProps,
  formatBirthdate,
  trackOrderSubmitted,
} from '@jane/kiosk/util';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { cartSize } from '@jane/shared-ecomm/util';
import { getJwt } from '@jane/shared/auth';
import type { Cart, StoreSpecial } from '@jane/shared/types';

const HeaderBox = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.grays.white,
  border: '1px solid rgba(0, 0, 0, 0.05)',
  borderRadius: '12px',
  boxShadow: '0px 2px 16px rgba(0, 0, 0, 0.05)',
}));

export const CheckoutFormOnDevice = () => {
  const [error, setError] = useState('');
  const [errorDetail, setErrorDetail] = useState<string | undefined>();
  const handleCheckoutRefusal = useCheckoutRefusal();
  const navigate = useNavigate();

  const { kiosk, storeId, store } =
    useTypedRouteLoaderData<RootLoaderType>('root');

  const [noteShown, setNoteShown] = useState(false);
  const [anonymousCheckout, setAnonymousCheckout] = useState(
    store.kiosk_anonymous_checkout === 'required'
  );
  const {
    cartToppers: { cartTopperIds },
    inlineAds: { inlineAdIds },
  } = useProductTracking();

  const { name: storeName } = store;

  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { data: cart, isLoading: isCartLoading, isSuccess } = useKioskCart();

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const { setCartInfo } = useCartInfoForTracking();
  const cartItemsCount = useMemo(() => cartSize(cart), [cart]);

  const { jdid } = useSessionContext();

  const allowAnonymousCheckout = store.kiosk_anonymous_checkout === 'allowed';
  const strictAnonymousCheckout = store.kiosk_strict_anonymous_checkout;

  const onAddNote = useCallback(() => {
    setNoteShown(true);
  }, []);

  const dmTypesInCart = useMemo(() => {
    const dmTypes = [];

    if (cartTopperIds.length > 0) {
      dmTypes.push('bag');
    }

    if (inlineAdIds.length > 0) {
      dmTypes.push('in-line');
    }

    return dmTypes;
  }, [cartTopperIds, inlineAdIds]);

  const onOrderError = useCallback(
    (error: unknown, params: CreateCheckoutParams) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate: params.birth_date,
          checkoutError: `${error}`,
          message: params.message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : params.phone,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod: null,
      });

      setErrorDetail(handleCheckoutRefusal(error));
      setError(String(error));
    },
    [
      anonymousCheckout,
      cart,
      kiosk,
      specials,
      store,
      dmTypesInCart,
      handleCheckoutRefusal,
    ]
  );

  const onOrderSuccess = useCallback(
    async (
      { reservation: { id, queue_number } }: CheckoutResponse,
      { birth_date, message, phone }: CreateCheckoutParams
    ) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate: birth_date,
          checkoutError: null,
          message: message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : phone,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod: null,
      });
      setCartInfo({
        cart,
        birthDate: birth_date,
        phoneNumber:
          anonymousCheckout || kiosk?.disable_phone_number_at_checkout
            ? null
            : phone,
        selectedBankAccount: null,
        signedInOrder: false,
      });

      //storing the queue number in local storage to ensure that it persists on the Order Checkout page
      localStorage.setItem('queueNumber', queue_number);

      navigate(`/stores/${storeId}/mobile/reservation/${id}/order-complete`, {
        replace: true,
      });
    },
    [
      cart,
      kiosk,
      specials,
      store,
      anonymousCheckout,
      dmTypesInCart,
      setCartInfo,
      navigate,
      storeId,
    ]
  );

  const { mutate: submitOrder, isPending: isSubmitting } = useSubmitOrder({
    onError: onOrderError,
    onSuccess: onOrderSuccess,
  });

  const onV3OrderError = useCallback(
    (error: unknown, variables: CheckoutProps) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate: variables.ageVerification?.birth_date,
          checkoutError: `${error}`,
          message: variables.message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : (variables.customer.phone ?? null),
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod: null,
      });
      setErrorDetail(handleCheckoutRefusal(error));
      setError(String(error));
    },
    [
      anonymousCheckout,
      cart,
      kiosk,
      specials,
      store,
      dmTypesInCart,
      handleCheckoutRefusal,
    ]
  );

  const onV3OrderSuccess = useCallback(
    async (
      { reservation: { id, queue_number } }: V3CheckoutResponse,
      variables: CheckoutProps
    ) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate: variables.ageVerification?.birth_date,
          checkoutError: null,
          message: variables.message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : (variables.customer.phone ?? null),
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod: null,
      });
      setCartInfo({
        cart,
        birthDate: variables.ageVerification?.birth_date,
        phoneNumber:
          anonymousCheckout || kiosk?.disable_phone_number_at_checkout
            ? null
            : (variables.customer.phone ?? null),
        selectedBankAccount: null,
        signedInOrder: false,
      });

      //storing the queue number in local storage to ensure that it persists on the Order Checkout page
      localStorage.setItem('queueNumber', String(queue_number ?? ''));

      updateLocalCartUuid(null);

      navigate(`/stores/${storeId}/mobile/reservation/${id}/order-complete`, {
        replace: true,
      });
    },
    [
      cart,
      kiosk,
      specials,
      store,
      anonymousCheckout,
      dmTypesInCart,
      setCartInfo,
      navigate,
      storeId,
    ]
  );

  const { mutate: v3Checkout, isPending: isV3Submitting } = useCheckout({
    onError: onV3OrderError,
    onSuccess: onV3OrderSuccess,
  });

  const formMethods = useForm<CheckoutFormFieldsDevice>({
    mode: 'onBlur',
  });

  const validateAndSubmit = async () => {
    const isValid = await formMethods.trigger();

    if (isValid) {
      handleSubmit();
    }
  };

  const handleSubmit = useCallback(async () => {
    const [message, phone, firstName, lastName, lastInitial, birthDate] =
      formMethods.getValues([
        'checkout-notes',
        'contact-num',
        'name-f',
        'name-l',
        'name-l',
        'b-day',
      ]);

    const name =
      strictAnonymousCheckout && anonymousCheckout
        ? 'ANONYMOUS CHECKOUT'
        : [firstName, lastName || lastInitial].join(' ');
    const resolvedPhone =
      anonymousCheckout || kiosk?.disable_phone_number_at_checkout ? '' : phone;
    const formattedBirthDate = birthDate
      ? formatBirthdate(birthDate)
      : undefined;

    if (useV3Cart) {
      v3Checkout(
        buildKioskCheckoutProps({
          cartUuid: cart.uuid,
          name,
          phone: resolvedPhone,
          birthDate: formattedBirthDate,
          message,
          deviceId: jdid,
          nowSeconds: Math.floor(Date.now() / 1000).toString(),
          janePay: null,
          jwt: getJwt(),
        })
      );
      return;
    }

    submitOrder({
      birth_date: formattedBirthDate,
      cart_uuid: cart.uuid,
      current_url: window.location.href,
      j_device_id: jdid,
      message,
      name,
      phone: resolvedPhone,
      // This is the v1 submit path (reached only when `useV3Cart` is false), so
      // `cart` is the v1 cart; narrow to read its v1 `products` shape.
      products: (cart as Cart).products,
      store_id: storeId,
    });
  }, [
    anonymousCheckout,
    cart,
    jdid,
    kiosk,
    storeId,
    strictAnonymousCheckout,
    submitOrder,
    formMethods,
    useV3Cart,
    v3Checkout,
  ]);

  return (
    <>
      <Flex width="100%">
        <HeaderBox width="100%" flexDirection="column" m={16} p={16}>
          <Typography mb={8} color="grays-mid">
            Kiosk order
          </Typography>
          <Typography variant="h3-semibold">{storeName}</Typography>
          {isSuccess && (
            <Typography variant="h3-regular">
              {pluralize({ noun: 'Item', number: cartItemsCount })} (
              {formatCurrency(cart.order_total)})
            </Typography>
          )}
        </HeaderBox>
      </Flex>
      <Flex flexDirection="column" px={24} pb={24} height="100%" width="100%">
        <BaseCustomerForm
          formMethods={formMethods}
          handleSubmit={validateAndSubmit}
        >
          <Typography variant="h2-semibold">Checkout</Typography>
          <Flex pt={8}>
            <Typography>
              {strictAnonymousCheckout && anonymousCheckout
                ? `${
                    kiosk?.show_checkout_message_field
                      ? 'Add a note or click'
                      : 'Click'
                  } to finalize your order.`
                : 'Some personal info needs to be collected in order for you to receive this order.'}
            </Typography>
          </Flex>
          {(!anonymousCheckout || !strictAnonymousCheckout) && (
            <>
              <Flex flexDirection="column" pt={16}>
                <Flex pb={4}>
                  <Typography variant="body-semibold">First name</Typography>
                </Flex>
                <FirstNameField autoComplete="given-name" labelHidden />
              </Flex>
              <Flex flexDirection="column" pt={16}>
                <Flex pb={4}>
                  <Typography variant="body-semibold">
                    Last {anonymousCheckout ? 'initial' : 'name'}
                  </Typography>
                </Flex>
                {anonymousCheckout ? (
                  <LastInitialField autoComplete="family-name" labelHidden />
                ) : (
                  <LastNameField autoComplete="family-name" labelHidden />
                )}
              </Flex>
              {!anonymousCheckout &&
                !kiosk?.disable_phone_number_at_checkout && (
                  <Flex flexDirection="column" pt={16}>
                    <Flex pb={4}>
                      <Typography variant="body-semibold">
                        Phone number
                      </Typography>
                    </Flex>
                    <PhoneNumberField autoComplete="tel" labelHidden />
                  </Flex>
                )}
              {!anonymousCheckout && !kiosk?.disable_birth_date_at_checkout && (
                <Flex flexDirection="column" pt={16}>
                  <Flex pb={4}>
                    <Typography variant="body-semibold">Birth date</Typography>
                  </Flex>
                  <BirthDateField
                    autoComplete="bday"
                    labelHidden
                    store={store}
                  />
                </Flex>
              )}
            </>
          )}

          {allowAnonymousCheckout && (
            <Flex flexDirection="row" pt={24}>
              <SwitchField
                label="Check out anonymously"
                name="anonymous checkout toggle"
                onChange={setAnonymousCheckout}
              />
            </Flex>
          )}

          {kiosk?.show_checkout_message_field && (
            <Flex pt={16}>
              {!noteShown && (
                <Link
                  color="grays-black"
                  ariaLabel="show note field"
                  onClick={onAddNote}
                >
                  <Typography>Add a note</Typography>
                </Link>
              )}
              {noteShown && <NotesField />}
            </Flex>
          )}

          <Flex pt={24}>
            <SubmitCustomerInfoButton
              disabled={isCartLoading}
              loading={
                isCartLoading || (useV3Cart ? isV3Submitting : isSubmitting)
              }
              size="default"
              onClick={() => validateAndSubmit()}
            />
          </Flex>
        </BaseCustomerForm>
      </Flex>
      {error && (
        <CartCheckoutError
          detail={errorDetail}
          error={error}
          onClose={() => {
            setError('');
            setErrorDetail(undefined);
          }}
        />
      )}
    </>
  );
};
